import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Info as InfoIcon } from 'lucide-react';
import { useTheme } from './hooks/useTheme.js';
import { useAuth } from './hooks/useAuth.js';
import AuthModal from './components/AuthModal.jsx';
import Landing from './components/landing/Landing.jsx';
import { REQUIRE_AUTH } from './utils/config.js';

const MapPage = lazy(() => import('./pages/MapPage.jsx'));
const BETA_MODE = ['true', '1'].includes(String(import.meta.env.VITE_BETA_MODE ?? '').toLowerCase());
const TOAST_ICONS = { success: CheckCircle2, warning: AlertTriangle, error: AlertCircle, info: InfoIcon };

function MapLoading() {
  return <div className="auth-splash"><div className="auth-splash-spinner" /><div className="auth-splash-text">CatRUS — загрузка карты…</div></div>;
}

function Toast({ notice, onClose }) {
  if (!notice) return null;
  const Icon = TOAST_ICONS[notice.type] || InfoIcon;
  return <div className={`toast ${notice.type}`} role="status"><Icon size={14} /><span>{notice.msg}</span><button onClick={onClose} className="toast-close" aria-label="Закрыть">✕</button></div>;
}

function AppLayout() {
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (notice) => setToast(notice);
  useEffect(() => {
    if (!toast || toast.type === 'error') return undefined;
    const timer = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const openAuth = (mode) => setAuthMode(mode);
  const closeAuth = () => { setAuthMode(null); setResetToken(null); setVerifyToken(null); };

  // Ссылки из email могут вести на /verify или /reset-password; модалка доступна поверх лендинга.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) return;
    const isReset = location.pathname.includes('reset-password') || params.get('mode') === 'reset';
    const isVerify = location.pathname.includes('/verify') || params.get('mode') === 'verify';
    if (isReset) {
      setResetToken(token);
      setAuthMode('reset');
    } else if (isVerify) {
      setVerifyToken(token);
      setAuthMode('verify');
    } else {
      return;
    }
    navigate('/', { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Карта — полноэкранное приложение без скролла (html/body/#root overflow:hidden в app.css).
  // Лендинг (и токен-флоу поверх него) должен скроллиться — вешаем класс на <html>,
  // который переопределяет overflow (см. landing.css). Снимаем на /map.
  useEffect(() => {
    const isLanding = location.pathname !== '/map';
    document.documentElement.classList.toggle('allow-page-scroll', isLanding);
    return () => document.documentElement.classList.remove('allow-page-scroll');
  }, [location.pathname]);

  const mapGateActive = REQUIRE_AUTH && location.pathname === '/map' && !auth.isAuthenticated;
  const tokenFlowActive = authMode === 'verify' || authMode === 'reset';
  if (mapGateActive && !auth.initialized && !tokenFlowActive) return <MapLoading />;

  const modal = authMode && !mapGateActive && <AuthModal auth={auth} mode={authMode} onSwitchMode={openAuth} resetToken={resetToken} verifyToken={verifyToken} onClose={closeAuth} showToast={showToast} />;

  if (mapGateActive) {
    return <div className="auth-gate"><AuthModal auth={auth} mode={authMode || 'login'} onSwitchMode={openAuth} resetToken={resetToken} verifyToken={verifyToken} onClose={() => setAuthMode('login')} showToast={showToast} /><Toast notice={toast} onClose={() => setToast(null)} /></div>;
  }

  return <>
    <Routes>
      <Route path="/" element={<Landing betaMode={BETA_MODE} themeMode={themeMode} onThemeChange={setThemeMode} onOpenAuth={openAuth} />} />
      <Route path="/verify" element={<Landing betaMode={BETA_MODE} themeMode={themeMode} onThemeChange={setThemeMode} onOpenAuth={openAuth} />} />
      <Route path="/reset-password" element={<Landing betaMode={BETA_MODE} themeMode={themeMode} onThemeChange={setThemeMode} onOpenAuth={openAuth} />} />
      <Route path="/map" element={<Suspense fallback={<MapLoading />}><MapPage auth={auth} themeMode={themeMode} setThemeMode={setThemeMode} onOpenAuth={openAuth} /></Suspense>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {modal}
    <Toast notice={toast} onClose={() => setToast(null)} />
  </>;
}

export default function App() { return <AppLayout />; }
